import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";

import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import StoreIcon from "@material-ui/icons/Store";
import PaymentIcon from "@material-ui/icons/Payment";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PaymentDetails from "./PaymentDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "../superFinance.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const PAY_FILTERS = `
  status: UNPAID
  type: $type
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
  driverName: $driverName
  driverPhoneNumber: $driverPhoneNumber
  shopName: $shopName
  shopPhoneNumber: $shopPhoneNumber
`;

const PAY_VARIABLES = `
  $limit: Int
  $skip: Int
  $type: PaymentType
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
  $driverName: String
  $driverPhoneNumber: String
  $shopName: String
  $shopPhoneNumber: String
`;

const GET_UNPAID = gql`
  query getUnpaidPaymentsByAdmin(${PAY_VARIABLES}) {
    getPaymentsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${PAY_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      _id
      for
      type
      status
      amount
      description
      createdAt
      updatedAt
      driver {
        _id
        fullName
        phoneNumber
      }
      shop {
        _id
        name
        phoneNumbers
      }
      user {
        _id
        fullName
      }
      trip {
        _id
      }
      order {
        _id
      }
    }
    getPaymentsByAdminCount(filters: { ${PAY_FILTERS} })
  }
`;

const GET_OUTSTANDING_COUNTS = gql`
  query getOutstandingCounts {
    all: getPaymentsByAdminCount(filters: { status: UNPAID })
    drivers: getPaymentsByAdminCount(filters: { status: UNPAID, type: PAY_TO_DRIVER })
    shops: getPaymentsByAdminCount(filters: { status: UNPAID, type: PAY_TO_SHOP })
  }
`;

const CREATE_TRANSACTION = gql`
  mutation createTransactionByAdmin($payments: [ID!]!, $for: TransactionFor!, $id: ID!) {
    createTransactionByAdmin(input: { payments: $payments, data: { for: $for, id: $id } }) {
      _id
      status
      amount
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const RECIPIENT_OPTIONS = [
  { value: "", label: "Drivers and shops" },
  { value: "PAY_TO_DRIVER", label: "Drivers only" },
  { value: "PAY_TO_SHOP", label: "Shops only" },
];
const RECIPIENT_LABELS = RECIPIENT_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "shop", label: "Shop" },
  { value: "driver", label: "Driver" },
];
const SEARCH_LABELS = { shop: "Shop", driver: "Driver" };

const SORT_OPTIONS = [
  { value: "oldest", label: "Oldest first" },
  { value: "newest", label: "Newest first" },
];

const FOR_LABELS = {
  DELIVERY: "Delivery",
  RIDE: "Ride",
  RESTAURANT: "Restaurant",
  PURCHASE: "Purchase",
};

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");
const money = (value) => `$${Number(value || 0).toFixed(2)}`;

/**
 * The payout target is decided by the payment TYPE, not by what the
 * payment was for. The previous version read `for` and mapped
 * ride/delivery to the driver and everything else to the shop, which
 * sent a PAY_TO_SHOP payment on a delivery order to the driver branch
 * and got rejected by the server.
 */
const recipientOf = (payment) => {
  if (payment.type === "PAY_TO_DRIVER" && payment.driver) {
    return { for: "driver", id: payment.driver._id, name: payment.driver.fullName, role: "Driver" };
  }
  if (payment.type === "PAY_TO_SHOP" && payment.shop) {
    return { for: "shop", id: payment.shop._id, name: payment.shop.name, role: "Shop" };
  }
  return null;
};

const readableError = (error) => {
  if (!error) return "Something went wrong.";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) return error.graphQLErrors[0].message;
  return error.message || "Something went wrong.";
};

/**
 * Pay - everything the platform still owes drivers and shops.
 *
 * The payout mutation has always accepted a list of payments and
 * required them to share one recipient, but the screen only ever sent
 * one payment at a time. Selecting rows and paying them together is the
 * same call used properly: the selection is grouped by recipient and one
 * payout is issued per group.
 */
const Pay = () => {
  const [searchTarget, setSearchTarget] = useState("shop");
  const [searchTerm, setSearchTerm] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("oldest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [confirmPayments, setConfirmPayments] = useState(null);
  const [paying, setPaying] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionResult, setActionResult] = useState("");

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: 1,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (looksLikePhone(term)) search[`${searchTarget}PhoneNumber`] = term;
        else search[`${searchTarget}Name`] = term;
      }
      setVariables((v) => ({
        ...v,
        skip: 0,
        driverName: undefined,
        driverPhoneNumber: undefined,
        shopName: undefined,
        shopPhoneNumber: undefined,
        ...search,
        type: recipientFilter || undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "newest" ? -1 : 1,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTarget, searchTerm, recipientFilter, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_UNPAID, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: countsData, refetch: refetchCounts } = useQuery(GET_OUTSTANDING_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [createTransactionByAdmin] = useMutation(CREATE_TRANSACTION);

  const payments = (data && data.getPaymentsByAdmin) || [];
  const totalCount = (data && data.getPaymentsByAdminCount) || 0;
  const counts = countsData || {};

  const pageAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const payablePayments = payments.filter((p) => recipientOf(p));

  /* ---------------- selection ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const payableIds = payablePayments.map((p) => p._id);
    setSelectedIds((current) => (current.length === payableIds.length ? [] : payableIds));
  };

  const checkboxState =
    payablePayments.length > 0 && selectedIds.length === payablePayments.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  /* ---------------- payouts ---------------- */

  // One payout per recipient, which is what the mutation expects: it
  // rejects a list whose payments do not all belong to the same driver
  // or shop.
  const groupByRecipient = (list) => {
    const groups = [];
    list.forEach((payment) => {
      const recipient = recipientOf(payment);
      if (!recipient) return;
      const key = `${recipient.for}:${recipient.id}`;
      const existing = groups.find((group) => group.key === key);
      if (existing) {
        existing.payments.push(payment);
        existing.amount += payment.amount || 0;
      } else {
        groups.push({ key, ...recipient, payments: [payment], amount: payment.amount || 0 });
      }
    });
    return groups;
  };

  const pendingGroups = confirmPayments ? groupByRecipient(confirmPayments) : [];
  const pendingTotal = pendingGroups.reduce((sum, group) => sum + group.amount, 0);

  const runPayouts = () => {
    setPaying(true);
    setActionError("");
    setActionResult("");

    const groups = pendingGroups;
    let succeeded = 0;
    const failures = [];

    // Sequential rather than parallel: each call moves real money, and a
    // burst of Stripe transfers is worth avoiding.
    const next = (index) => {
      if (index >= groups.length) {
        setPaying(false);
        setConfirmPayments(null);
        setSelectedIds([]);
        refetch();
        refetchCounts();
        if (failures.length) {
          setActionError(
            `${succeeded} of ${groups.length} payouts went through. ${failures.join(" ")}`
          );
        } else {
          setActionResult(
            `Paid ${groups.length} recipient${groups.length === 1 ? "" : "s"}, ${money(pendingTotal)} in total.`
          );
        }
        return;
      }
      const group = groups[index];
      createTransactionByAdmin({
        variables: {
          for: group.for,
          id: group.id,
          payments: group.payments.map((payment) => payment._id),
        },
      })
        .then(() => {
          succeeded += 1;
          next(index + 1);
        })
        .catch((err) => {
          failures.push(`${group.name || group.role}: ${readableError(err)}`);
          next(index + 1);
        });
    };

    next(0);
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (recipientFilter) {
    activeFilterChips.push({ id: "recipient", label: RECIPIENT_LABELS[recipientFilter] });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "recipient") setRecipientFilter("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setRecipientFilter("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superFinancePage superPayPage">
        <main className="main">
          <PageHeader title="To be paid" description="Everything Spark still owes drivers and shops." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedPayments = payments.filter((p) => selectedIds.includes(p._id));

  return (
    <div className="page superFinancePage superPayPage">
      <main className="main">
        <PageHeader
          title="To be paid"
          count={totalCount}
          description="Everything Spark still owes drivers and shops. Select rows to pay several at once."
        />

        <section className="stats">
          <MetricCard icon={<AccountBalanceIcon />} title="Outstanding payments" value={counts.all || 0} tone="warning" />
          <MetricCard icon={<DirectionsCarIcon />} title="Owed to drivers" value={counts.drivers || 0} />
          <MetricCard icon={<StoreIcon />} title="Owed to shops" value={counts.shops || 0} />
          <MetricCard
            icon={<PaymentIcon />}
            title="On this page"
            value={money(pageAmount)}
            hint={`${payments.length} payment${payments.length === 1 ? "" : "s"}`}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()} name or phone number...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={recipientFilter ? RECIPIENT_LABELS[recipientFilter] : "Recipient"}
              options={RECIPIENT_OPTIONS}
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="primary"
              size="sm"
              icon={<PaymentIcon fontSize="small" />}
              onClick={() => setConfirmPayments(selectedPayments)}
            >
              Pay selected
            </Button>
          }
          leftContent={
            payments.length > 0 ? (
              <span className="superFinancePill">This page: {money(pageAmount)}</span>
            ) : null
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}
        {actionResult && (
          <div className="dsFormMessage success" role="status">
            {actionResult}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superFinanceRefetchSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every payable row on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Recipient</div>
              <div className="tableHeaderCell">For</div>
              <div className="tableHeaderCell">Amount</div>
              <div className="tableHeaderCell">Waiting since</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {payments.length === 0 ? (
              <EmptyState
                icon={<AccountBalanceIcon />}
                title={activeFilterChips.length === 0 ? "Nothing outstanding" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Every driver and shop has been paid what they are owed."
                    : "No unpaid payments match these filters. Try widening or clearing them."
                }
              />
            ) : (
              payments.map((payment) => {
                const recipient = recipientOf(payment);
                return (
                  <div className="row" key={payment._id}>
                    <Checkbox
                      size="small"
                      disabled={!recipient}
                      checked={selectedIds.includes(payment._id)}
                      onChange={() => toggleSelectOne(payment._id)}
                      inputProps={{ "aria-label": `Select payment ${payment._id}` }}
                    />

                    <div className="superFinanceParty" data-label="Recipient">
                      {recipient ? (
                        <React.Fragment>
                          <span className="superFinancePartyName">{recipient.name || "Unnamed"}</span>
                          <span className="superFinancePartyRole">{recipient.role}</span>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <span className="superFinancePartyName">No payout target</span>
                          <span className="superFinancePartyRole">
                            {(payment.type || "").replace(/_/g, " ").toLowerCase()}
                          </span>
                        </React.Fragment>
                      )}
                    </div>

                    <div className="superFinanceTypeCell" data-label="For">
                      <span>{FOR_LABELS[payment.for] || payment.for || "-"}</span>
                      {payment.description && (
                        <span className="superFinanceSubtext">{payment.description}</span>
                      )}
                    </div>

                    <span className="superFinanceAmount" data-label="Amount">
                      {money(payment.amount)}
                    </span>

                    <span className="updated" data-label="Waiting since">
                      <strong>{formatDate(payment.createdAt)}</strong>
                      <span>{formatTime(payment.createdAt)}</span>
                    </span>

                    <div className="superPayActions">
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={!recipient || paying}
                        onClick={() => setConfirmPayments([payment])}
                      >
                        Pay
                      </Button>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setPaymentDetail(payment)}
                        title="View payment"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(confirmPayments)}
        onClose={() => (paying ? null : setConfirmPayments(null))}
        title={pendingGroups.length > 1 ? "Send payouts" : "Send payout"}
        actions={[
          { label: "Cancel", onClick: () => setConfirmPayments(null), disabled: paying },
          {
            label: paying ? "Paying" : `Pay ${money(pendingTotal)}`,
            onClick: runPayouts,
            variant: "primary",
            loading: paying,
            disabled: pendingGroups.length === 0,
          },
        ]}
      >
        <div className="superPayoutSummary">
          <span className="superPayoutIntro">
            {pendingGroups.length > 1
              ? `This sends ${pendingGroups.length} separate payouts, one per recipient. Each is charged on its own, so one failing does not stop the rest.`
              : "This sends the payout immediately and cannot be undone."}
          </span>
          <div className="superPayoutList">
            {pendingGroups.map((group) => (
              <div className="superPayoutRow" key={group.key}>
                <span className="superPayoutRowText">
                  <span className="superPayoutRowName">{group.name || "Unnamed"}</span>
                  <span className="superPayoutRowMeta">
                    {group.role} · {group.payments.length} payment
                    {group.payments.length === 1 ? "" : "s"}
                  </span>
                </span>
                <span className="superPayoutRowAmount">{money(group.amount)}</span>
              </div>
            ))}
          </div>
          <div className="superPayoutTotal">
            <span>Total</span>
            <span>{money(pendingTotal)}</span>
          </div>
        </div>
      </DialogShell>

      <DialogShell
        open={Boolean(paymentDetail)}
        onClose={() => setPaymentDetail(null)}
        title="Payment details"
      >
        {paymentDetail && (
          <PaymentDetails
            close={() => setPaymentDetail(null)}
            refetch={() => refetch()}
            payment={paymentDetail}
            hasResolver={true}
          />
        )}
      </DialogShell>
    </div>
  );
};

export default Pay;
